// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import type { DidUrl } from '@zcloak/did-resolver/types';
import type { ZkpGenResponse } from '@zcloak/login-rpc/rpcs';

import React, { createContext, useCallback, useEffect, useState } from 'react';

import { isDidUrl } from '@zcloak/did/utils';
import { ZkidWalletProvider as Provider } from '@zcloak/login-providers';
import { VerifiablePresentation } from '@zcloak/vc/types';

import { isRelease } from './config';
import { program } from './program';

interface State {
  didUrl?: DidUrl;
  provider?: Provider;
  login: () => void;
  generateZkp: () => Promise<ZkpGenResponse | undefined>;
  requestContentVp: () => Promise<VerifiablePresentation>;
  requestDigestVp: () => Promise<VerifiablePresentation>;
}

export const ZkidWalletContext = createContext<State>({} as State);
const readyPromise = new Promise<void>((resolve) => {
  if (document.readyState === 'complete') {
    resolve();
  } else {
    window.addEventListener('load', () => resolve());
  }
});

function ZkidWalletProvider({ children }: { children: React.ReactNode }) {
  const [didUrl, setDidUrl] = useState<DidUrl>();
  const [provider, setProvider] = useState<Provider>();

  const login = useCallback(async () => {
    if (!provider) return;

    await provider.requestAuth();

    await provider.getCurrentDid().then(({ didUri }) => {
      if (isDidUrl(didUri)) {
        setDidUrl(didUri);
      }
    });
  }, [provider]);

  const generateZkp = useCallback(async () => {
    if (!provider) return;

    const result = await provider.generateZkp({
      ctype: isRelease
        ? '0x4394e5a3f6d7e18957d02095d46e37558e2502bce59aacd407b074781d7d6b5b'
        : '0xddd55777a3be0f8b848b94ddd98c2c4716add21dc50787ac65534b37f1b5bba5',
      attester: didUrl,
      program
    });

    return result;
  }, [didUrl, provider]);

  const requestContentVp = useCallback(async () => {
    if (!provider) return;

    const result = await provider.requestCredentialContent(undefined as any, ['Champion Team']);

    return result;
  }, [provider]);

  const requestDigestVp = useCallback(async () => {
    if (!provider) return;
    const result = await provider.requestCredentialDigest(
      undefined as any,
      isRelease
        ? '0x4394e5a3f6d7e18957d02095d46e37558e2502bce59aacd407b074781d7d6b5b'
        : '0xddd55777a3be0f8b848b94ddd98c2c4716add21dc50787ac65534b37f1b5bba5',
      didUrl
    );

    return result as any;
  }, [provider, didUrl]);

  // init provider
  useEffect(() => {
    readyPromise.then(() => {
      const _provider = new Provider();

      setProvider(_provider);

      return _provider;
    });
  }, []);

  // auto get did info
  useEffect(() => {
    if (!provider) return;

    provider.isAuth().then((authed) => {
      if (authed) {
        provider.getCurrentDid().then(({ didUri }) => {
          if (isDidUrl(didUri)) {
            setDidUrl(didUri);
          }
        });
      }
    });
  }, [provider]);

  // listen changed
  useEffect(() => {
    if (!provider) return;

    const onChange = () => {
      provider.getCurrentDid().then(({ didUri }) => {
        if (isDidUrl(didUri)) {
          setDidUrl(didUri);
        }
      });
    };

    provider.on('did_changed', onChange);

    return () => {
      provider.off('did_changed', onChange);
    };
  }, [provider]);

  return (
    <ZkidWalletContext.Provider
      value={{ didUrl, login, provider, generateZkp, requestContentVp, requestDigestVp }}
    >
      {children}
    </ZkidWalletContext.Provider>
  );
}

export default ZkidWalletProvider;
